import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { fetchWlsPayment, postWlsProcessSecurityDeposit, fetchWlsLynnbrookConfig } from '../../utils/wls-api-client';
import isWlsBookingNotFoundError from '../../utils/wls-booking-error';
import {
  Box,
  Container,
  Divider,
  FlexBox,
  PageStateMessage,
  Panel,
  Spacer,
  TextBody,
  TextDense,
  TextH2
} from '@directsoftware/ui-kit-web-admin';
import ReactI18n from 'react-i18n';
import moment from 'moment/moment';
import Script from 'react-load-script';
import { Redirect } from 'react-router-dom';
import {
  selectPaymentState,
  updatePaymentSlice
} from '../../redux/slices/payment';
import PageHeader from '../shared/page-header';
import ListingInfo from '../shared/listing-info';
import PaymentBreakdown from '../shared/payment-breakdown';
import RemainingTotal from '../shared/remaining-total';
import { useDetectMobile } from '../../../shared/hooks/useDetectMobile';
import StripeV2 from '../stripe/stripe-v2';
import FooterFinal from '../layout/footer-final';
import PaymentTransaction from '../errors/payment-transaction';
import { selectUiState, updateUiSlice } from '../../redux/slices/ui';
import FormV2 from './form-v2';
import { formatCurrency } from '../../../shared/helpers';
import { balanceDueWhen } from '../../utils/payment-terms';
import {
  balanceAfterCharge as balanceAfterChargeFor,
  chargeSettlesBooking as chargeSettlesBookingFor,
  isFullyPaid as isFullyPaidFor,
  remainingBalance as remainingBalanceFor,
  remainingBalanceNotice as remainingBalanceNoticeFor
} from '../../utils/payment-amount-hierarchy';
import LynnbrookPaymentV2 from '../lynnbrook/lynnbrook-payment-v2';
import ConfirmationHeader from '../receipt/shared/confirmation-header';

const PaymentV2 = props => {
  const { isLargeTabletOrGreater } = useDetectMobile();
  const dispatch = useDispatch();
  const brand = useSelector(state => state.brand);
  const paymentState = useSelector(selectPaymentState);
  const { paymentStateChanged } = useSelector(selectUiState);
  const translate = ReactI18n.getIntlMessage;
  const [isLynnbrook, setIsLynnbrook] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const updatePaymentState = (data, isFilterChange = false) => {
    dispatch(updatePaymentSlice(data));
    if (isFilterChange) dispatch(updateUiSlice({ paymentStateChanged: true }));
  };

  const handleStripeScriptError = () => {
    updatePaymentState({
      isStripeSuccessful: false
    });
  };

  const handleStripeScriptLoad = () => {
    Stripe.setPublishableKey(paymentState.stripe_publishable_key);
    updatePaymentState({
      isStripeSuccessful: true,
      loading: false
    });
  };

  const getChargeAmount = () => {
    return Math.max(0, paymentState.bookingPaymentDueToday).toFixed(2);
  };

  const balanceDueWhenText = balanceDueWhen(
    paymentState.bookingPaymentBalanceDueOn,
    paymentState.bookingPaymentBalanceDueDate,
    brand.date_format,
    translate
  );

  const remainingBalance = () => remainingBalanceFor(paymentState.booking);

  const isFullyPaid = () => isFullyPaidFor(paymentState.booking);

  const balanceAfterCharge = () =>
    balanceAfterChargeFor(paymentState.booking, getChargeAmount());

  // The balance left after this charge is secondary information: it gets its
  // own due date, and is only the "final payment" when this charge settles the
  // booking, which the caller checks before asking for this notice.
  const remainingBalanceNotice = amount => {
    const notice = remainingBalanceNoticeFor({
      amount,
      dueWhenText: balanceDueWhenText,
      autoCollected: paymentState.bookingPaymentAutoCollected,
      formatAmount: balance =>
        formatCurrency(balance.toFixed(2), brand.currency)
    });
    return notice ? translate(notice.key, notice.values) : '';
  };

  const within48HoursOfCheckIn = () => {
    const checkIn = moment(paymentState.booking.check_in, 'YYYY-MM-DD');
    const rightNow = moment();
    const duration = moment.duration(rightNow.diff(checkIn));
    return Math.abs(duration.asHours()) <= 48;
  };

  const moreChargesNeeded = () => {
    const {
      isStripeSuccessful,
      securityDepositRequired,
      booking,
      charges
    } = paymentState;
    if (!isStripeSuccessful || booking.cancelled || !booking.confirmed) {
      return false;
    }
    if (
      securityDepositRequired &&
      charges.length > 0 &&
      moment() > moment(booking.check_in).subtract(4, 'days')
    ) {
      return (
        charges.findIndex(
          c => c.is_security_deposit === true && c.status !== 'failed'
        ) === -1
      );
    }
    return charges.length === 0;
  };

  const failedSecurityDeposit = () => {
    const { charges } = paymentState;
    const securityDeposits = charges.filter(
      c => c.is_security_deposit === true
    );
    if (securityDeposits.length === 0) {
      return false;
    }
    return securityDeposits.every(c => c.status === 'failed');
  };

  const shouldShowSecDepForm = () => {
    // Only surface the security deposit authorization form when the booking
    // actually requires a deposit. Otherwise the collect-a-payment case is
    // already handled by the payment form above, and showing this would render
    // a redundant "Process Authorization" section for bookings with no deposit.
    const { securityDepositRequired } = paymentState;
    if (
      securityDepositRequired &&
      within48HoursOfCheckIn() &&
      moreChargesNeeded()
    ) {
      return true;
    } else if (failedSecurityDeposit()) {
      return true;
    } else {
      return false;
    }
  };

  const isCollectingPayment = () => {
    return !isFullyPaid() && getChargeAmount() !== (0).toFixed(2);
  };

  const chargeSettlesBooking = () =>
    isCollectingPayment() &&
    chargeSettlesBookingFor(paymentState.booking, getChargeAmount());

  const canReusePaymentCardForDeposit = () => {
    // When the guest is already entering a card to pay their balance on this same
    // page, reuse that card for the deposit hold and do not render the separate
    // deposit-only form below. The fallback card form is only shown for
    // deposit-only pages where no balance payment is being collected.
    return (
      paymentState.securityDepositRequired &&
      isCollectingPayment() &&
      shouldShowSecDepForm()
    );
  };

  const securityDepositHoldNotice = () => {
    const rawAmount =
      paymentState.securityDeposit &&
      paymentState.securityDeposit.calculation_amount;
    const amount = rawAmount
      ? `${formatCurrency(rawAmount, brand.currency)} `
      : '';
    return (
      `A refundable ${amount}security deposit hold will be placed on the ` +
      `card you enter above, closer to your check-in date. This works exactly ` +
      `like a hotel hold — your card won't be charged this amount unless ` +
      `there is damage to the property. You don't need to enter your card ` +
      `details again.`
    );
  };

  const applyPaymentData = response => {
    const data = response.data;
    if (!data || !data.booking) {
      setNotFound(true);
      updatePaymentState({ loading: false });
      return;
    }
    const lynnbrookData = {
      accountTargetId: data.lynnbrook_target_id,
      accountPersonId: data.lynnbrook_account_person_id,
      sessionId: data.lynnbrook_session_id,
      token: null
    };
    delete data.lynnbrook_target_id;
    delete data.lynnbrook_account_person_id;
    delete data.lynnbrook_session_id;
    updatePaymentState(
      {
        loading: false,
        ...data,
        accountPersonCreated: true,
        lynnbrookErrors: [],
        fetchingLynnbrookConfig: false,
        lynnbrookConfig: lynnbrookData,
        lynnbrookCardAccepted: false
      },
      true
    );
  };

  const fetchData = () => {
    setNotFound(false);
    setLoadError(false);
    updatePaymentState({ loading: true });
    fetchWlsPayment(brand, props.match.params.booking_code)
      .then(applyPaymentData)
      .catch(error => {
        console.error(error);
        if (isWlsBookingNotFoundError(error)) {
          setNotFound(true);
        } else {
          setLoadError(true);
          toast.error(`error loading page ${error}`);
        }
        updatePaymentState({ loading: false });
      });
  };

  const chargeSecurityDeposit = token => {
    const depositData = {
      listing_id: paymentState.listing.id,
      booking_id: paymentState.booking.id,
      booking_code: paymentState.booking.booking_code,
      customer_email: paymentState.customerEmail,
      customer_name: paymentState.customerName,
      customer_telephone: paymentState.customerTelephone,
      stripe_token: token
    };

    postWlsProcessSecurityDeposit(brand, depositData)
      .then(response => {
        updatePaymentState({
          charges: [...paymentState.charges, { is_security_deposit: true }],
          isSubmitted: true
        });
      })
      .catch(error => {
        return toast.error(`unable to process security deposit ${error}`);
      });
  };

  const handleStripeSuccess = json => {
    const token = json.id;
    chargeSecurityDeposit(token);
  };

  const handleStripeFailure = ({ error }) => {
    console.error(error);
    updatePaymentState({ transactionError: error });
  };

  const handleStripeCallback = (statusCode, json) => {
    if (statusCode === 200) {
      handleStripeSuccess(json);
    } else {
      handleStripeFailure(json);
    }
  };

  const createStripeToken = () => {
    Stripe.card.createToken(
      {
        number: paymentState.cardNumber,
        cvc: paymentState.cardCvv,
        name: paymentState.customerName,
        exp: paymentState.cardExpiry,
        address_zip: paymentState.customerPostalCode || 'invalid'
      },
      handleStripeCallback
    );
  };

  const processSecurityDeposit = (
    cardNumber,
    cardExpiry,
    cardCvv,
    customerEmail,
    customerName,
    customerPostalCode,
    customerTelephone
  ) => {
    updatePaymentState(
      {
        cardNumber: cardNumber.replace(' ', ''),
        cardExpiry,
        cardCvv: cardCvv.replace(' ', ''),
        customerEmail,
        customerName,
        customerPostalCode,
        customerTelephone,
        createStripeToken: true
      },
      true
    );
  };

  const fetchLynnbrookConfig = targetId => {
    const name = `${paymentState.customerFirstName} ${
      paymentState.customerLastName
    }`;
    const applyConfig = res => {
      const data = res.data;
      updatePaymentState({
        fetchingLynnbrookConfig: false,
        accountPersonCreated: true,
        lynnbrookConfig: {
          ...paymentState.lynnbrookConfig,
          accountPersonId: data.account_person_id,
          sessionId: data.session_id,
          token: process.env.APTEXX_API_TOKEN
        }
      });
    };

    fetchWlsLynnbrookConfig(brand, paymentState.listing.id, {
      customer_email: paymentState.customer.email,
      customer_name: name,
      customer_telephone: paymentState.customer.telephone,
      target_id: targetId
    })
      .then(applyConfig)
      .catch(error => { console.warn(error); });
  };

  const showOnlySecDepLynnbrook = () => {
    return (
      isLynnbrook &&
      shouldShowSecDepForm() &&
      (getChargeAmount() === (0).toFixed(2) || isFullyPaid())
    );
  };

  const showOnlyProcessPaymentLynnbrook = () => {
    return (
      isLynnbrook &&
      !shouldShowSecDepForm() &&
      getChargeAmount() !== (0).toFixed(2)
    );
  };

  useEffect(
    () => {
      if (paymentStateChanged) {
        dispatch(updateUiSlice({ paymentStateChanged: false }));
        if (paymentState.createStripeToken) {
          updatePaymentState({ createStripeToken: false });
          createStripeToken();
        }
      }
    },
    [paymentState, paymentStateChanged]
  );

  useEffect(() => {
    fetchData();
    setIsLynnbrook(brand.organization.payment_processor === 2);
  }, []);

  if (paymentState.isSubmitted) {
    return (
      <Redirect
        to={{
          pathname: `/my-bookings/receipt/${paymentState.booking.booking_code}`
        }}
      />
    );
  }

  if (paymentState.loading) return null;

  if (notFound) {
    return (
      <Container variation="extended-wls">
        <PageStateMessage
          iconProps={{ name: 'emptySet' }}
          headline="Booking Not Found"
          description="We couldn't find a booking with that confirmation code. Please check the link and try again."
        />
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container variation="extended-wls">
        <PageStateMessage
          iconProps={{ name: 'exclamationTriangle' }}
          headline="Unable to Load Booking"
          description="We couldn't load your booking details. Please try again later."
        />
      </Container>
    );
  }

  if (paymentState.booking && paymentState.booking.cancelled) {
    return (
      <Container variation="extended-wls">
        <PageStateMessage
          iconProps={{ name: 'emptySet' }}
          headline="Booking Cancelled"
          description="This booking has been cancelled, so no payment is required. Please contact us if you have any questions."
        />
      </Container>
    );
  }

  return (
    <>
      <Script
        url="https://js.stripe.com/v2/"
        onError={handleStripeScriptError}
        onLoad={handleStripeScriptLoad}
      />
      <Container variation="extended-wls">
        <PageHeader
          label="Make a Payment"
          backLink={`/my-bookings/receipt/${paymentState.booking.booking_code}`}
        />
        <FlexBox className="detailsLayout checkoutLayout">
          <Box flex="1">
            {(!isFullyPaid() && getChargeAmount() !== (0).toFixed(2)) ||
            showOnlySecDepLynnbrook() ? (
              <>
                <ConfirmationHeader usePaymentState />
                {!showOnlySecDepLynnbrook() && (
                  <>
                    <Box>
                      In order to complete your reservation, please enter your
                      payment details below.
                    </Box>
                    <Spacer size="m" />
                    <FlexBox justifyContent="space-between" alignItems="center">
                      <Box>
                        <TextH2>
                          {translate(`cx.payment.amount_due_now`)}
                        </TextH2>
                      </Box>
                      <Box>
                        <TextH2>
                          {formatCurrency(getChargeAmount(), brand.currency)}
                        </TextH2>
                      </Box>
                    </FlexBox>
                    {chargeSettlesBooking() ? (
                      <TextDense textColor="light-gray">
                        {translate(`cx.payment.settles_booking`)}
                      </TextDense>
                    ) : (
                      remainingBalanceNotice(balanceAfterCharge()) && (
                        <TextDense textColor="light-gray">
                          {remainingBalanceNotice(balanceAfterCharge())}
                        </TextDense>
                      )
                    )}
                  </>
                )}
                <Spacer size="l" />
                {isLynnbrook ? (
                  <>
                    <Divider padding="s" />
                    <LynnbrookPaymentV2
                      usePaymentState
                      onlySecDep={showOnlySecDepLynnbrook()}
                      onlyProcessPayment={showOnlyProcessPaymentLynnbrook()}
                      accountPersonCreated={paymentState.accountPersonCreated}
                      availability={paymentState.availability}
                      booking={paymentState.booking}
                      brand_info={brand.brand_info}
                      chargeAmount={getChargeAmount()}
                      listing={paymentState.listing}
                      slug={paymentState.slug}
                      customerEmail={paymentState.customerEmail}
                      customerName={`${paymentState.customerFirstName} ${
                        paymentState.customerLastName
                      }`}
                      currency={brand.currency}
                      customerPostalCode={paymentState.adrPostalCode}
                      customerTelephone={paymentState.customerTelephone}
                      translate={translate}
                      unit={paymentState.unit}
                      lynnbrookConfig={paymentState.lynnbrookConfig}
                      adrStreet={paymentState.adrStreet}
                      adrCity={paymentState.adrCity}
                      adrState={paymentState.adrState}
                      adrCountry={paymentState.adrCountry}
                      adrPostalCode={paymentState.adrPostalCode}
                      updateState={updatePaymentState}
                      fetchLynnbrookConfig={fetchLynnbrookConfig}
                    />
                  </>
                ) : (
                  <StripeV2
                    hideChargeAmountSummary
                    currency={brand.currency}
                    availability={paymentState.availability}
                    booking={paymentState.booking}
                    brand_info={brand.brand_info}
                    brand={brand}
                    chargeAmount={getChargeAmount()}
                    listing={paymentState.listing}
                    slug={paymentState.slug}
                    stripeCustomerId={paymentState.stripe_customer_id}
                    stripeIntentId={paymentState.stripe_intent_id}
                    stripePublishableKey={paymentState.stripe_publishable_key}
                    stripeAccountID={paymentState.stripe_account_id}
                    translate={translate}
                    unit={paymentState.unit}
                  />
                )}
                <PaymentTransaction errors={[paymentState.transactionError]} />
              </>
            ) : isFullyPaid() ? (
              <>
                <TextH2>You're all paid up!</TextH2>
                <Spacer />
                <TextBody>
                  No additional payments are needed, enjoy your stay!
                </TextBody>
              </>
            ) : (
              <>
                <TextH2>No payment is due right now</TextH2>
                <Spacer />
                <TextBody>
                  {remainingBalanceNotice(remainingBalance()) ||
                    'Your remaining balance will be due closer to your stay.'}
                </TextBody>
              </>
            )}
            {shouldShowSecDepForm() &&
              !isLynnbrook &&
              (canReusePaymentCardForDeposit() ? (
                <section className="payment">
                  <Spacer />
                  <Box>{securityDepositHoldNotice()}</Box>
                </section>
              ) : (
                <section className="payment">
                  {paymentState.securityDepositRequired ? (
                    <div>
                      <Spacer />
                      <Box>
                        Please enter your billing details below so we can
                        process your damage deposit authorization. This
                        authorization works exactly the same way as when you
                        check in to a hotel, and your card will not be charged
                        this amount unless there is damage to the property.
                      </Box>
                    </div>
                  ) : (
                    <Box>
                      Please enter your billing details below so we can process
                      your reservation.
                    </Box>
                  )}
                  <Spacer size="m" />
                  <FormV2
                    processSecurityDeposit={processSecurityDeposit}
                    slug={paymentState.slug}
                    rental_agreement={paymentState.rental_agreement}
                  />
                  <PaymentTransaction
                    errors={[paymentState.transactionError]}
                  />
                </section>
              ))}
          </Box>
          {isLargeTabletOrGreater && (
            <Box className="detailsLayout__right checkoutLayout">
              <Panel addShadow>
                <Box padding="s">
                  <ListingInfo stateObject={paymentState} />
                </Box>
                <Divider />
                <Box padding="s">
                  <PaymentBreakdown stateObject={paymentState} />
                </Box>
                <Divider />
                <Box padding="s">
                  <RemainingTotal
                    priceTotal={paymentState.booking.price_total}
                    pricePaid={paymentState.booking.price_paid}
                    balanceDueDate={paymentState.bookingPaymentBalanceDueDate}
                    balanceDueOn={paymentState.bookingPaymentBalanceDueOn}
                  />
                </Box>
              </Panel>
            </Box>
          )}
        </FlexBox>
      </Container>
      <Spacer size="xxl" />
      <FooterFinal />
    </>
  );
};

export default PaymentV2;
